// build: drums — the step-grid beat builder. Rows come from config.rows
// (default kick / snare / hat), 16 toggle columns each; ▶/■ loops the pattern
// via startGroove and edits land in the loop live (the transport reads the
// same pattern object every step). config.startFrom preloads a DRUM_PATTERNS
// starter so lessons can hand the learner a heartbeat to grow.
//
// Soft checks (moment.checks) run against { pattern } — warm nudges, never
// verdicts: "save keeper" lights when they all pass, and "keep it anyway" is
// ALWAYS offered when one doesn't (same conventions as BuildMoment). Saving
// calls onKeeper(keeperId, keeperName, { pattern }) and unlocks Continue.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { runChecks } from "../../../lib/homeKeyTheory";
import { resumeAudio } from "../../../lib/homeKeyAudio";
import {
  DRUM_PATTERNS,
  STEPS_PER_BAR,
  playHat,
  playKick,
  playSnare,
  startGroove,
} from "../../../lib/homeKeyGroove";

const DEFAULT_ROWS = ["kick", "snare", "hat"];
const ROW_LABELS = { kick: "kick", snare: "snare", hat: "hat", hatOpen: "open hat" };
const STRONG_STEPS = [0, 4, 8, 12];

// One-shot preview when a cell toggles on while the loop is stopped.
const previewRow = (row) => {
  if (row === "kick") playKick(0);
  else if (row === "snare") playSnare(0);
  else playHat(0, row === "hatOpen");
};

const copyPattern = (pattern) =>
  Object.fromEntries(
    Object.entries(pattern || {}).map(([row, steps]) => [row, [...(steps || [])]])
  );

export default function DrumBuild({ moment, onReadyChange, onKeeper }) {
  const config = moment.config || {};
  const rows = config.rows || DEFAULT_ROWS;
  const bpm = config.bpm || 92;
  const swing = config.swing || 0.5;

  const [pattern, setPattern] = useState(() => {
    const start = config.startFrom ? DRUM_PATTERNS[config.startFrom] : null;
    const base = copyPattern(start);
    rows.forEach((row) => {
      if (!base[row]) base[row] = [];
    });
    return base;
  });
  const [playing, setPlaying] = useState(false);
  const [now, setNow] = useState(-1);
  const [saved, setSaved] = useState(false);

  // The transport reads this one object; keeping its identity stable and
  // swapping its arrays makes toggles audible mid-loop.
  const liveRef = useRef(copyPattern(pattern));
  const grooveRef = useRef(null);

  const stopGroove = useCallback(() => {
    if (grooveRef.current && grooveRef.current.stop) grooveRef.current.stop();
    grooveRef.current = null;
    setPlaying(false);
    setNow(-1);
  }, []);
  useEffect(() => () => stopGroove(), [stopGroove]);

  const ready = saved;
  useEffect(() => {
    if (onReadyChange) onReadyChange(ready);
  }, [ready, onReadyChange]);

  const totalHits = rows.reduce((n, row) => n + (pattern[row] || []).length, 0);

  const results = useMemo(
    () => (totalHits > 0 ? runChecks(moment.checks || [], { pattern }) : null),
    [totalHits, moment.checks, pattern]
  );
  const allPass = results != null && results.every((r) => r.pass);

  const toggleCell = (row, step) => {
    if (saved) return;
    resumeAudio();
    const steps = new Set(pattern[row] || []);
    const turningOn = !steps.has(step);
    if (turningOn) steps.add(step);
    else steps.delete(step);
    const next = { ...pattern, [row]: [...steps].sort((a, b) => a - b) };
    liveRef.current[row] = next[row];
    setPattern(next);
    if (turningOn && !grooveRef.current) previewRow(row);
  };

  const toggleGroove = () => {
    if (grooveRef.current) {
      stopGroove();
      return;
    }
    if (!totalHits) return;
    resumeAudio();
    Object.keys(pattern).forEach((row) => {
      liveRef.current[row] = pattern[row];
    });
    grooveRef.current = startGroove({
      pattern: liveRef.current,
      bpm,
      swing,
      onStep: (step) => setNow(step % STEPS_PER_BAR),
    });
    setPlaying(true);
  };

  const save = () => {
    if (!totalHits) return;
    stopGroove();
    onKeeper(moment.keeperId, moment.keeperName, { pattern: copyPattern(pattern) });
    setSaved(true);
  };

  const steps = Array.from({ length: STEPS_PER_BAR }, (_, i) => i);

  return (
    <div className="hk-moment-body">
      <p>
        {config.prompt ||
          "Build your beat: tap cells to place hits, then loop it. The grid is one bar — columns 1, 5, 9, and 13 are the strong beats."}
      </p>

      <div className="hk-step-grid" role="group" aria-label="drum step grid">
        {rows.map((row) => (
          <div className="hk-drum-row" key={row}>
            <span className="hk-drum-row-label">{ROW_LABELS[row] || row}</span>
            <div className="hk-step-row">
              {steps.map((step) => {
                const on = (pattern[row] || []).includes(step);
                const strong = STRONG_STEPS.includes(step);
                return (
                  <button
                    key={step}
                    type="button"
                    className={`hk-step${strong ? " hk-step--strong" : ""}${
                      on ? " hk-step--on" : ""
                    }${now === step ? " hk-step--now" : ""}`}
                    aria-pressed={on}
                    aria-label={`${ROW_LABELS[row] || row}, step ${step + 1}${
                      on ? ": on" : ": off"
                    }`}
                    onClick={() => toggleCell(row, step)}
                    disabled={saved}
                  />
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <div className="hk-choice-row">
        <button
          type="button"
          className={`hk-demo-btn${playing ? " hk-demo-btn--on" : ""}`}
          disabled={!totalHits}
          onClick={toggleGroove}
        >
          {playing ? "■ stop the loop" : "▶ loop it"}
        </button>
      </div>

      {!saved && results && (
        <div className="hk-checks">
          {results.map((r) => (
            <div
              key={r.id}
              className={`hk-check-note${r.pass ? " hk-check-note--pass" : ""}`}
            >
              {r.pass ? "✓ " : "~ "}
              {r.message}
            </div>
          ))}
        </div>
      )}

      {!saved && (
        <div className="hk-choice-row">
          <button
            type="button"
            className="hk-btn hk-btn--primary"
            disabled={!totalHits || !allPass}
            onClick={save}
          >
            save keeper
          </button>
          {totalHits > 0 && !allPass && (
            <button type="button" className="hk-btn hk-btn--ghost" onClick={save}>
              actually, I like it — keep it anyway
            </button>
          )}
        </div>
      )}

      {saved && (
        <p className="hk-echo-status">
          saved to your track: <strong>{moment.keeperName}</strong>. It's part of
          the music now.
        </p>
      )}
    </div>
  );
}
